import Input, { InputVariant } from '@ben/orion/input';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../../shared/constants';

interface IModularPensionSlidersInputProps {
  componentControllerName: string;
}

const ModularPensionSlidersInput = ({
  componentControllerName,
}: IModularPensionSlidersInputProps) => {
  const { control, watch } = useFormContext();

  return (
    <>
      <ComponentsWrapper>
        <Controller
          control={control}
          name={`${componentControllerName}.employeeMin`}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Employee min value accepted"
              hintText="Defines the minimum amount the employee is allowed to select."
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              type="number"
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
            validate: (value) => {
              const employeeMaxValue = watch(
                `${componentControllerName}.employeeMax`
              );
              if (
                employeeMaxValue &&
                Number(value) > Number(employeeMaxValue)
              ) {
                return 'Employee min value should be less than max value';
              }
              return true;
            },
          }}
        />
        <Controller
          control={control}
          name={`${componentControllerName}.employeeMax`}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Employee max value accepted"
              hintText="Defines the maximum amount the employee is allowed to select."
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              type="number"
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
            validate: (value) => {
              const employeeMinValue = watch(
                `${componentControllerName}.employeeMin`
              );
              if (
                employeeMinValue &&
                Number(value) < Number(employeeMinValue)
              ) {
                return 'Employee max value should be greater than min value';
              }
              return true;
            },
          }}
        />
        <Controller
          control={control}
          name={`${componentControllerName}.employeeDefault`}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Employee default value"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              type="number"
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
            validate: (value) => {
              const employeeMinValue = watch(
                `${componentControllerName}.employeeMin`
              );
              const employeeMaxValue = watch(
                `${componentControllerName}.employeeMax`
              );
              if (
                employeeMinValue &&
                employeeMaxValue &&
                (Number(value) < Number(employeeMinValue) ||
                  Number(value) > Number(employeeMaxValue))
              ) {
                return 'Employee default value should be between min and max values';
              }
              return true;
            },
          }}
        />
      </ComponentsWrapper>
      <ComponentsWrapper>
        <Controller
          control={control}
          name={`${componentControllerName}.employerMin`}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Employer min value accepted"
              hintText="Defines the minimum amount of employer contribution the employee is allowed to select"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              type="number"
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
            validate: (value) => {
              const employerMaxValue = watch(
                `${componentControllerName}.employerMax`
              );
              if (
                employerMaxValue &&
                Number(value) > Number(employerMaxValue)
              ) {
                return 'Employer min value should be less than or equal to max value';
              }
              return true;
            },
          }}
        />
        <Controller
          control={control}
          name={`${componentControllerName}.employerMax`}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Employer max value accepted"
              hintText="Defines the minimum amount of employer contribution the employee is allowed to select"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              type="number"
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
            validate: (value) => {
              const employerMinValue = watch(
                `${componentControllerName}.employerMin`
              );
              if (
                employerMinValue &&
                Number(value) < Number(employerMinValue)
              ) {
                return 'Employer max value should be greater than or equal to min value';
              }
              return true;
            },
          }}
        />
        <Controller
          name={`${componentControllerName}.increment`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Increment"
              hintText="Set the amount by which the slider moves when adjusted."
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              defaultValue={1}
              type="number"
              min={0.01}
              step={0.01}
            />
          )}
        />
      </ComponentsWrapper>
      <ComponentsWrapper>
        <Controller
          control={control}
          name={`${componentControllerName}.hintText`}
          render={({ field }) => (
            <Input
              {...field}
              variant={InputVariant.TextArea}
              name="Hint message (optional)"
              hintText="A hint message is a brief piece of text that appears when the user interacts with the question mark (?) icon.It can be used to provide additional explanations, further clarifications or helpful tips."
              width="100%"
              maxLength={150}
            />
          )}
        />
      </ComponentsWrapper>
    </>
  );
};

export default ModularPensionSlidersInput;

const ComponentsWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;
